import { useQuery } from '@tanstack/react-query';
import {
    RichEditor,
    formatAbsolute,
    formatRelative,
    TIMELINE_HISTORY_STALE_MS
} from '@odo/shared-ui';
import type { TiptapDoc } from '@odo/shared-ui';
import { tiptapDocFromString } from '../../patient-files/actions/dx-report/editor/editorState';
import { EMPTY_TIPTAP_DOC } from '../../patient-files/actions/dx-report/templates';
import { fetchTimeline } from '../../../services/timeline';

export type TriggerKind = 'refinement' | 'retention';

const NOOP_CHANGE = () => {};
const NOOP_UPLOAD = async (): Promise<{ url: string; fileId: string }> => ({ url: '', fileId: '' });
const KIND_TITLES: Record<TriggerKind, string> = {
    refinement: 'Refinement',
    retention: 'Retention'
};

interface TriggerNotes {
    doc: TiptapDoc;
    hasNotes: boolean;
    author: string;
    createdAt: string;
}

export function ByLine({ author, createdAt }: { author: string; createdAt: string }) {
    return (
        <span className="text-[11px] text-gray-500 flex items-center gap-2">
            <span className="truncate max-w-[160px]">{author}</span>
            <span className="text-gray-300">·</span>
            <span title={formatAbsolute(createdAt)} className="whitespace-nowrap">
                {formatRelative(createdAt) || formatAbsolute(createdAt)}
            </span>
        </span>
    );
}

function useTriggerNotes(patientId: string, kind: TriggerKind) {
    return useQuery({
        queryKey: [`${kind}-trigger-notes`, patientId],
        queryFn: async (): Promise<TriggerNotes | null> => {
            const res = await fetchTimeline(patientId, {
                type: `${kind}_trigger`,
                isLatest: true,
                limit: 1
            });
            const event = res.data[0];
            if (!event) return null;
            const payload = event.payload as { notesDoc?: TiptapDoc; notes?: string } | undefined;
            const doc =
                payload?.notesDoc ?? (payload?.notes ? tiptapDocFromString(payload.notes) : null);
            return {
                doc: doc ?? EMPTY_TIPTAP_DOC,
                hasNotes: Boolean(payload?.notesDoc) || Boolean(payload?.notes),
                author: event.createdByUser?.name ?? event.createdBy ?? 'Unknown',
                createdAt: event.createdAt
            };
        },
        staleTime: TIMELINE_HISTORY_STALE_MS,
        enabled: Boolean(patientId)
    });
}

// The latest refinement_trigger / retention_trigger ODO notes, read-only.
export function TriggerNotesCard({ patientId, kind }: { patientId: string; kind: TriggerKind }) {
    const { data: trigger, isLoading, isError } = useTriggerNotes(patientId, kind);

    return (
        <div className="flex-1 min-h-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
            <div className="px-5 py-2.5 border-b border-gray-100 flex items-center justify-between gap-2 flex-shrink-0">
                <span className="font-bold text-sm text-gray-800">
                    {KIND_TITLES[kind]} trigger notes
                </span>
                {trigger && <ByLine author={trigger.author} createdAt={trigger.createdAt} />}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-5 py-3">
                {isLoading ? (
                    <div className="text-xs text-gray-400 italic">Loading {kind} notes…</div>
                ) : isError ? (
                    <div className="text-xs text-red-500">
                        Failed to load the {kind} trigger notes.
                    </div>
                ) : !trigger || !trigger.hasNotes ? (
                    <div className="text-xs text-gray-400 italic">
                        No {kind} notes were recorded.
                    </div>
                ) : (
                    <RichEditor
                        value={trigger.doc}
                        readOnly
                        chrome="flush"
                        onChange={NOOP_CHANGE}
                        onImageUpload={NOOP_UPLOAD}
                    />
                )}
            </div>
        </div>
    );
}
